import React from 'react';
import { Alert } from '@/components/ui/alert';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Form, Input, SubmitButton } from '../../../../components/form';
import InputLabel from '../../../../components/form/InputLabel';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { Link } from '../../../../Router';
import { routes } from '../../routes';
import { HttpErrorCode } from '../../../../api/jwtAuth';

export type LoginHistoryLocationState = {
  alertMessage?: 'reset-password' | 'forgot-password' | 'change-email';
};

export const AlertMessage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const history = useHistory<LoginHistoryLocationState>();

  switch (history.location.state?.alertMessage) {
    case 'reset-password': {
      return <Alert variant="info">{t('signIn.info.passwordReset')}</Alert>;
    }

    case 'forgot-password': {
      return <Alert variant="info">{t('signIn.info.forgotPassword')}</Alert>;
    }

    case 'change-email': {
      return <Alert variant="info">{t('signIn.info.changeEmail')}</Alert>;
    }

    default: {
      return <></>;
    }
  }
};

export type LoginFormData = {
  email: string;
  password: string;
};

const defaultValues: LoginFormData = {
  email: '',
  password: '',
};

const formSchema: yup.ObjectSchema<LoginFormData> = yup.object({
  email: yup.string().trim().required().email(),
  password: yup.string().required().min(8),
});

const loginErrorTranslationKeys: Partial<Record<HttpErrorCode, string>> = {
  ORGANIZATION_NOT_FOUND: 'signIn.errors.organizationNotFound',
  ORGANIZATION_ACCESS_DENIED: 'signIn.errors.organizationAccessDenied',
};

type LoginPasswordFormCommonProps = {
  onSubmit: (data: LoginFormData) => void;
  loginErrors: boolean;
  loginErrorCode?: HttpErrorCode;
};

const LoginPasswordFormCommon: React.FC<
  React.PropsWithChildren<LoginPasswordFormCommonProps>
> = ({ onSubmit, loginErrors, loginErrorCode }) => {
  const { t } = useTranslation();

  return (
    <Form
      onSubmit={onSubmit}
      validationSchema={formSchema}
      defaultValues={defaultValues}>
      <Input
        label={t('form.general.email.label')}
        placeholder={t('form.general.email.placeholder')}
        name="email"
        autoFocus
      />

      <Input
        placeholder={t('form.general.password.placeholder')}
        name="password"
        type="password"
        labelComponent={
          <Row>
            <Col>
              <InputLabel label={t('form.general.password.label')} />
            </Col>
            <Col className="col-auto">
              <Link
                to={routes.forgotPassword.route}
                className="form-text small text-muted"
                tabIndex={-1}>
                {t('signIn.forgotPasswordLink')}
              </Link>
            </Col>
          </Row>
        }
      />

      {loginErrors ? (
        <p className="text-danger">
          {t(
            loginErrorCode && loginErrorTranslationKeys[loginErrorCode]
              ? loginErrorTranslationKeys[loginErrorCode]!
              : 'signIn.invalid',
          )}
        </p>
      ) : (
        <></>
      )}

      <SubmitButton block>{t('signIn.button')}</SubmitButton>
    </Form>
  );
};

export default LoginPasswordFormCommon;
